import { ActionIcon, Group, Stack } from '@mantine/core';

import { Icon, Text, Title } from '@repo/foundations';
import type { NodeProps } from '@xyflow/react';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { EntityIcon } from '../EntityIcon/EntityIcon';
import { useEntityNodeHeader } from '../EntityNode/EntityNode.hooks';
import type { LineageFlowNode } from '../EntityNode/EntityNode.types';
import { useStyles } from './EntityNodeHeader.styles';
import EntityNodeHeaderMenu from './EntityNodeHeaderMenu';

function EntityNodeHeader({
	data,
	selected,
}: Pick<NodeProps<LineageFlowNode>, 'data' | 'selected'>) {
	const { classes } = useStyles();

	const {
		id,
		title,
		subtitle,
		entity_type,
		icon,
		integration_type,
		direction,
		upstream_count,
		downstream_count,
		upstream_fetched,
		downstream_fetched,
	} = data;

	const {
		creationQueries,
		monitors,
		tests,
		isChartNode,
		isRootNode,
		isFocused,
		isCurrentModal,
		isFullscreen,
		showCreationQuery,
		showMonitorsAndTests,
		hasMonitorsOrTests,
		areMonitorsAndTestsSuccessful,
		nodeActions,
		handleOpenCreationQuery,
		handleOpenMonitorsAndTests,
		handleCloseAllModals,
		handleViewImpactingColumns,
	} = useEntityNodeHeader(id);

	const canCollapseConnections = useMemo(
		() =>
			!isRootNode &&
			!isFocused &&
			(direction === 'UPSTREAM' ? upstream_count : downstream_count) > 0 &&
			!(direction === 'UPSTREAM' ? upstream_fetched : downstream_fetched) &&
			!isNil(direction),
		[
			isRootNode,
			isFocused,
			upstream_count,
			downstream_count,
			upstream_fetched,
			downstream_fetched,
			direction,
		]
	);

	const handleNodeClick = useCallback(() => {
		nodeActions.nodeClick?.onClick(id);
	}, [nodeActions.nodeClick, id]);

	const actionIcon = hasMonitorsOrTests ? (
		<ActionIcon size="sm" onClick={handleOpenMonitorsAndTests}>
			<Icon
				name={
					areMonitorsAndTestsSuccessful ? 'circleCheck' : 'exclamationCircle'
				}
				color={
					areMonitorsAndTestsSuccessful
						? 'icon/success/default'
						: 'icon/critical/default'
				}
			/>
		</ActionIcon>
	) : null;

	return (
		<Group className={classes.wrapper}>
			<EntityIcon
				icon={icon ? (icon as string) : undefined}
				entityType={entity_type}
				integrationType={integration_type ?? undefined}
			/>
			<Group className={classes.content}>
				<Stack className={classes.text} onClick={handleNodeClick}>
					<Title size="sm" truncate>
						{title}
					</Title>
					<Text size="xs" color="text/secondary/default">
						{subtitle}
					</Text>
				</Stack>
				<Group className={classes.actions}>
					{actionIcon}
					<EntityNodeHeaderMenu
						id={id}
						canCollapseConnections={canCollapseConnections}
						direction={direction}
						creationQueries={creationQueries}
						monitors={monitors}
						tests={tests}
						isChartNode={isChartNode}
						isRootNode={isRootNode}
						isFocused={isFocused}
						isSelected={Boolean(selected)}
						isCurrentModal={isCurrentModal}
						isFullscreen={isFullscreen}
						showCreationQuery={showCreationQuery}
						showMonitorsAndTests={showMonitorsAndTests}
						hasMonitorsOrTests={hasMonitorsOrTests}
						nodeActions={nodeActions}
						handleOpenMonitorsAndTests={handleOpenMonitorsAndTests}
						handleOpenCreationQuery={handleOpenCreationQuery}
						handleCloseAllModals={handleCloseAllModals}
						handleViewImpactingColumns={handleViewImpactingColumns}
					/>
				</Group>
			</Group>
		</Group>
	);
}

const ObservedEntityNodeHeader = observer(EntityNodeHeader);

export default ObservedEntityNodeHeader;
